/**
 * Histogram binning and the cumulative curve (D529). Edges are NICE (1, 2, 5 × 10ⁿ) so the axis reads cleanly; the last
 * bin is closed on both sides, every other bin is `[from, to)`.
 */
import { isFiniteNumber, roundCoord } from './numbers.js';
import { niceStep } from './ticks.js';
const MAX_BINS = 200;
/** Bin finite numbers. No finite value → no bins. */
export function histogram(values, options = {}) {
    let finite = values.filter(isFiniteNumber);
    const clip = options.domain;
    if (clip !== undefined)
        finite = finite.filter((v) => v >= clip[0] && v <= clip[1]);
    if (finite.length === 0)
        return { bins: [], total: 0, min: 0, max: 0, step: 1 };
    let min = finite[0];
    let max = min;
    for (const v of finite) {
        if (v < min)
            min = v;
        if (v > max)
            max = v;
    }
    const lo = clip?.[0] ?? min;
    const hi = clip?.[1] ?? max;
    const wanted = Math.min(40, Math.max(3, Math.round(options.bins ?? Math.ceil(Math.log2(finite.length)) + 1)));
    const span = hi - lo;
    let step = isFiniteNumber(options.binWidth) && options.binWidth > 0 ? options.binWidth : span > 0 ? niceStep(span, wanted) : 1;
    // Guard a pathological binWidth: never more than MAX_BINS bins.
    if (span / step > MAX_BINS)
        step = niceStep(span, MAX_BINS);
    const first = Math.floor(lo / step + 1e-9) * step;
    const count = Math.max(1, Math.ceil((hi - first) / step - 1e-9));
    const counts = new Array(count).fill(0);
    for (const v of finite) {
        const i = Math.min(count - 1, Math.max(0, Math.floor((v - first) / step + 1e-9)));
        counts[i] = (counts[i] ?? 0) + 1;
    }
    const bins = counts.map((c, index) => ({ index, from: roundCoord(first + index * step, 10), to: roundCoord(first + (index + 1) * step, 10), count: c, share: c / finite.length }));
    return { bins, total: finite.length, min, max, step };
}
/** The cumulative curve of a histogram: one point per bin, ending at the total. */
export function cumulativeCurve(h) {
    let run = 0;
    return h.bins.map((b) => {
        run += b.count;
        return { x: b.to, count: run, share: h.total > 0 ? run / h.total : 0 };
    });
}
/** The `p`-quantile (0 … 1) of finite values by linear interpolation, or `null` for no data. */
export function quantile(values, p) {
    const sorted = values.filter(isFiniteNumber).sort((a, b) => a - b);
    if (sorted.length === 0)
        return null;
    const at = Math.min(1, Math.max(0, p)) * (sorted.length - 1);
    const lo = Math.floor(at);
    const hi = Math.ceil(at);
    return (sorted[lo] ?? 0) + ((sorted[hi] ?? 0) - (sorted[lo] ?? 0)) * (at - lo);
}
//# sourceMappingURL=histogram.js.map